import { useEffect } from 'react';
import { X, Shield, Mail, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import { useLanguage } from '../contexts/LanguageContext';
import { privacyContent, privacyRights } from '../constants/privacyContent';
import { CONTACT_INFO } from '../constants/navigation';
import { copyTextToClipboard } from '../utils/copyToClipboard';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PrivacyPolicyModal({ isOpen, onClose }: PrivacyPolicyModalProps) {
  const { t } = useLanguage();

  const handleCopyPrivacyEmail = async () => {
    const ok = await copyTextToClipboard(CONTACT_INFO.privacyEmail);
    if (ok) {
      toast.success(t('common.copiedToClipboard'));
    } else {
      toast.error(t('common.copyFailed'));
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isFrench = t('nav.language') === 'EN';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />
      
      <div className="relative z-10 w-full max-w-4xl max-h-[90vh] transition-all duration-300 transform scale-100">
        <Card className="border-[#f91bdc]/20 shadow-2xl overflow-hidden">
          <CardHeader className="relative bg-gradient-to-r from-[#f91bdc]/5 to-[#f91bdc]/10 border-b border-[#f91bdc]/20">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onClose}
              className="absolute right-4 top-4 h-8 w-8 p-0 hover:bg-white/50 z-10"
            >
              <X size={18} />
            </Button>
            
            <div className="flex items-center space-x-3 pr-12">
              <div className="w-12 h-12 bg-[#f91bdc] text-white rounded-lg flex items-center justify-center">
                <Shield size={24} />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold text-gray-900">
                  {t('privacy.title')}
                </CardTitle>
                <p className="text-gray-600 mt-1">
                  {t('privacy.subtitle')}
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2 mt-4">
              <Badge variant="outline" className="border-[#f91bdc] text-[#f91bdc]">
                {t('privacy.gdprCompliant')}
              </Badge>
              <Badge variant="outline" className="border-[#f91bdc] text-[#f91bdc]">
                {t('privacy.lastUpdated').replace('{year}', new Date().getFullYear().toString())}
              </Badge>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            <ScrollArea className="h-[60vh] p-6">
              <div className="space-y-8">
                {/* Introduction */}
                <section>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 border-l-4 border-[#f91bdc] pl-4">
                    {t('privacy.introduction')}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {t('privacy.introductionText')}
                  </p>
                </section>

                {/* Privacy Sections */}
                {privacyContent.map((section) => (
                  <section key={section.id}>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 border-l-4 border-[#f91bdc] pl-4">
                      {isFrench ? section.titleFr : section.title}
                    </h3>
                    
                    {section.content && (
                      <p className="text-gray-700 leading-relaxed mb-4">
                        {isFrench ? section.contentFr : section.content}
                      </p>
                    )}
                    
                    {section.items && (
                      <div className={section.id === 'storageSecurity' ? 'bg-[#f91bdc]/5 rounded-lg p-4 space-y-3' : 'bg-gray-50 rounded-lg p-4 space-y-3'}>
                        {section.items.map((item, itemIndex) => (
                          <div key={itemIndex} className="flex items-start space-x-3">
                            {section.id === 'storageSecurity' ? (
                              <Shield size={18} className="text-[#f91bdc] mt-1" />
                            ) : (
                              <div className="w-2 h-2 bg-[#f91bdc] rounded-full mt-2"></div>
                            )}
                            <span className="text-gray-700">
                              {isFrench ? item.fr : item.en}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                ))}

                {/* Your Rights */}
                <section>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 border-l-4 border-[#f91bdc] pl-4">
                    {t('privacy.yourRights')}
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    {privacyRights.map((right, index) => (
                      <div key={index} className="bg-[#f91bdc]/5 rounded-lg p-4 text-center">
                        <h4 className="font-semibold text-gray-900 mb-2">
                          {isFrench ? right.titleFr : right.title}
                        </h4>
                        <p className="text-sm text-gray-700">
                          {isFrench ? right.descriptionFr : right.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Changes to Policy */}
                <section>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 border-l-4 border-[#f91bdc] pl-4">
                    {t('privacy.changesToPolicy')}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {t('privacy.changesToPolicyText')}
                  </p>
                </section>

                {/* Contact */}
                <section>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 border-l-4 border-[#f91bdc] pl-4">
                    {t('privacy.contact')}
                  </h3>
                  <div className="bg-[#f91bdc]/10 rounded-lg p-6">
                    <p className="text-gray-700 leading-relaxed mb-4">
                      {t('privacy.contactText')}
                    </p>
                    <div className="flex items-center gap-2 text-[#f91bdc] flex-wrap">
                      <Mail size={18} />
                      <span className="font-medium">{CONTACT_INFO.privacyEmail}</span>
                      <button
                        type="button"
                        onClick={handleCopyPrivacyEmail}
                        className="inline-flex items-center gap-1 text-sm font-medium hover:text-[#e015c9] underline-offset-2 hover:underline"
                      >
                        <Copy size={14} aria-hidden />
                        {t('common.copyEmail')}
                      </button>
                    </div>
                  </div>
                </section>
              </div>
            </ScrollArea>
          </CardContent>

          <div className="border-t border-gray-200 p-4 bg-gray-50">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">
                {t('privacy.lastUpdatedDate').replace('{date}', new Date().toLocaleDateString())}
              </p>
              <Button 
                onClick={onClose}
                className="bg-[#f91bdc] hover:bg-[#e015c9] text-white px-6"
              >
                {t('privacy.close')}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}